import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { getCompany } from './helpers/commonFunctions';
import { globalLoader } from './helpers/global';
import { GetPermission } from './services/company_panel/userService';
import Header from './components/company_panel/common/header';
import SideBar from './components/company_panel/common/sidebar';
import Footer from './components/company_panel/common/footer'
import { SOCKET_CLIENT } from './utils/socket';
import { SOCKET_EVENTS } from './utils/Constants';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';



const CompanyLayout = () => {
  const currentRoomIdRef = useRef(null);
  // remembers export jobs we've already downloaded, so a completion event that is
  // delivered more than once (clustered socket / re-connect) doesn't trigger the file
  // download twice — which Chrome blocks as "multiple files downloaded automatically".
  const downloadedExportJobsRef = useRef(new Set());

  useEffect(()=>{
    globalLoader(true);
    
    Promise.all([
      import("./assets/company/css/bootstrap.min.css"),
      import("./assets/company/css/style.css"),
    ]).then(()=>{
      globalLoader(false);
    }).catch((error)=>{
      console.log("Error in CSS",error);
    })
    
    GetPermission(getCompany().company_id,getCompany().id).then(data => {
      localStorage.setItem("permission", JSON.stringify(data.data.response.moduleAssignedPermissions));
    }).catch(error => {
        console.log("error=====>", error)
    })

  },[]);

  // useMemo(() => {
  //     document.addEventListener('brand-changed', function (data) {
  //         setIsBrandChanged(!isBrandChanged);
  //         console.log('-------chaing brand', data);
  //         joinSocket(data.detail.outlet_id);
  //     });
  //     document.addEventListener('outlet-changed', function (data) {
  //         setIsOutletChanged(!isOutletChanged);
  //         console.log('-------chaing outlet', data);
  //         joinSocket(data.detail.outlet_id);
  //     });
  // });

  // Join the company register id room (used for company wide events like report exports).
  // Re-join on every (re)connect so we always stay in the room even after a socket drop.
  useEffect(() => {
    const joinCompanyRoom = () => {
      try {
        const companyId = JSON.parse(localStorage.getItem("company_detail"))?.company_id;
        const userId = JSON.parse(localStorage.getItem("company_user"))?.id;
        if (companyId) {
          SOCKET_CLIENT.emit('JOIN-USER', { roomID: `${companyId}-${userId}` });
          console.log('------Joined company room', companyId);
        }
      } catch (error) {
        console.log('Error joining company room', error);
      }
    };

    // join now (socket.io buffers the emit until connected) and again on every reconnect
    joinCompanyRoom();
    SOCKET_CLIENT.on('connect', joinCompanyRoom);

    // Export completed / failed are handled globally so the user gets the file (or the
    // failure toast) no matter which page they navigated to after triggering the export.
    // each exportable report emits its own progress/completed/failed event names
    const exportProgressEvents = [
      SOCKET_EVENTS.REPORTS.SALES.EXPORT_PROGRESS,
      SOCKET_EVENTS.REPORTS.ITEM_COST_CALCULATION.EXPORT_PROGRESS,
      SOCKET_EVENTS.ERP.SALES.EXPORT_PROGRESS,
      SOCKET_EVENTS.ERP.PAYMENTS.EXPORT_PROGRESS,
      SOCKET_EVENTS.ERP.COMMISSIONS.EXPORT_PROGRESS,
      SOCKET_EVENTS.REPORTS.ZATCA.EXPORT_PROGRESS,
      SOCKET_EVENTS.REPORTS.DISCOUNT.EXPORT_PROGRESS,
      SOCKET_EVENTS.REPORTS.REFUND.EXPORT_PROGRESS,
      SOCKET_EVENTS.REPORTS.WASTE.EXPORT_PROGRESS,
      SOCKET_EVENTS.REPORTS.PAYMENT.EXPORT_PROGRESS,
      SOCKET_EVENTS.REPORTS.STOCK_MOVEMENT.EXPORT_PROGRESS,
    ];
    const exportCompletedEvents = [
      SOCKET_EVENTS.REPORTS.SALES.EXPORT_COMPLETED,
      SOCKET_EVENTS.REPORTS.ITEM_COST_CALCULATION.EXPORT_COMPLETED,
      SOCKET_EVENTS.ERP.SALES.EXPORT_COMPLETED,
      SOCKET_EVENTS.ERP.PAYMENTS.EXPORT_COMPLETED,
      SOCKET_EVENTS.ERP.COMMISSIONS.EXPORT_COMPLETED,
      SOCKET_EVENTS.REPORTS.ZATCA.EXPORT_COMPLETED,
      SOCKET_EVENTS.REPORTS.DISCOUNT.EXPORT_COMPLETED,
      SOCKET_EVENTS.REPORTS.REFUND.EXPORT_COMPLETED,
      SOCKET_EVENTS.REPORTS.WASTE.EXPORT_COMPLETED,
      SOCKET_EVENTS.REPORTS.PAYMENT.EXPORT_COMPLETED,
      SOCKET_EVENTS.REPORTS.STOCK_MOVEMENT.EXPORT_COMPLETED,
    ];
    const exportFailedEvents = [
      SOCKET_EVENTS.REPORTS.SALES.EXPORT_FAILED,
      SOCKET_EVENTS.REPORTS.ITEM_COST_CALCULATION.EXPORT_FAILED,
      SOCKET_EVENTS.ERP.SALES.EXPORT_FAILED,
      SOCKET_EVENTS.ERP.PAYMENTS.EXPORT_FAILED,
      SOCKET_EVENTS.ERP.COMMISSIONS.EXPORT_FAILED,
      SOCKET_EVENTS.REPORTS.ZATCA.EXPORT_FAILED,
      SOCKET_EVENTS.REPORTS.DISCOUNT.EXPORT_FAILED,
      SOCKET_EVENTS.REPORTS.REFUND.EXPORT_FAILED,
      SOCKET_EVENTS.REPORTS.WASTE.EXPORT_FAILED,
      SOCKET_EVENTS.REPORTS.PAYMENT.EXPORT_FAILED,
      SOCKET_EVENTS.REPORTS.STOCK_MOVEMENT.EXPORT_FAILED,
    ];

    const handleExportSocket = (response) => {
      const data = response?.data;
      if (!data) return;
      // progress ticks are shown as a bar on the reports page only — never toast them
      if (exportProgressEvents.includes(response?.event)) return;

      if (exportCompletedEvents.includes(response?.event)) {
        // guard against the same completion event arriving more than once
        const jobKey = data.jobId || data.job_id || data.url;
        if (jobKey) {
          if (downloadedExportJobsRef.current.has(jobKey)) return;
          downloadedExportJobsRef.current.add(jobKey);
        }
        // show the "completed" toast only once the file has finished downloading
        const showCompletedToast = () => {
          Swal.fire({
            customClass: 'swal-wide',
            icon: 'success',
            title: response?.message || 'Export completed',
            showConfirmButton: false,
            timer: 3000,
            toast: true,
            position: 'top-right',
          });
        };
        if (data.url) {
          // download within the app (no new tab): fetch the file as a blob and save it
          const filename = data.filename || 'export.xlsx';
          fetch(data.url)
            .then((res) => res.blob())
            .then((blob) => {
              saveAs(blob, filename);
              showCompletedToast();
            })
            .catch((err) => {
              console.log('Export download failed, falling back to direct save', err);
              saveAs(data.url, filename);
              showCompletedToast();
            });
        } else {
          showCompletedToast();
        }
      } else if (exportFailedEvents.includes(response?.event)) {
        Swal.fire({
          customClass: 'swal-wide',
          icon: 'error',
          title: data.error || response?.message || 'Export failed',
          showConfirmButton: false,
          timer: 3000,
          toast: true,
          position: 'top-right',
        });
      }
    };
    SOCKET_CLIENT.on('GET-DATA', handleExportSocket);

    return () => {
      SOCKET_CLIENT.off('connect', joinCompanyRoom);
      SOCKET_CLIENT.off('GET-DATA', handleExportSocket);
    };
  }, []);

  //this will triggered when any brand or outlet will be changed
  useEffect(() => {
    // This runs when the component mounts
    document.addEventListener('brand-changed', joinSocket);
    document.addEventListener('outlet-changed', joinSocket);

    // Cleanup when the component unmounts
    return () => {
      document.removeEventListener('brand-changed', joinSocket);
      document.removeEventListener('outlet-changed', joinSocket);

      // Leave the last joined room
      if (currentRoomIdRef.current) {
        SOCKET_CLIENT.emit('LEAVE-USER', { roomID: currentRoomIdRef.current });
        currentRoomIdRef.current = null; // ✅ Clear reference
      }
    };
  }, []);

  function joinSocket(data){
    try {
      const roomId = `${JSON.parse(localStorage.getItem("company_detail")).company_id}-${data.detail.outlet_id}`;

      //checking iF new room is same as old room, if yes than just return
      if(currentRoomIdRef.current && currentRoomIdRef.current == roomId){
        return;
      }

      // 🔁 Leave old room 
      if(currentRoomIdRef.current){
        const leaveRequest = {
          roomID: currentRoomIdRef.current
        };
        const joinRequest = {
          roomID: roomId
        };
        leaveRoom(leaveRequest, joinRequest);
      }else{
        //join new room
        const joinRequest = {
          roomID: roomId
        };
        joinRoom(joinRequest);
      }

      
      // SOCKET_CLIENT.emit('JOIN-USER', joinRequest, (error) => {
      //   if (error) {
      //     console.error(`Error joining room ${roomId} : `,  error);
      //   }
      //   else{
      //       console.log("Joined room : ", roomId);
      //       currentRoomIdRef.current = roomId;
      //   }
      // });
    } catch (error) {
      
    }

  }

  function joinRoom(joinRequest){
    SOCKET_CLIENT.emit('JOIN-USER', joinRequest);
    currentRoomIdRef.current = joinRequest.roomID;
    console.log('------New room', currentRoomIdRef.current);
  }
  function leaveRoom(leaveRequest, joinRequest){
    SOCKET_CLIENT.emit('LEAVE-USER', leaveRequest, (err) => {
      if (err) {
        console.error("Error leaving room:", err);
        currentRoomIdRef.current = null;
      } else {
        console.log("------Left room:", leaveRequest.roomID);
        currentRoomIdRef.current = null;

        joinRoom(joinRequest);
      }
    });
  }
  // useEffect(()=>{
  //   let joinRequest = {
  //     roomID: JSON.parse(localStorage.getItem("company_detail")).company_id
  //   };

  //   SOCKET_CLIENT.emit('JOIN-USER', joinRequest, (error) => {
  //     if (error) {
  //       console.error("*****Socket connection error on JOIN-USER", error);
  //     }
  //     else{
  //         console.log("joined")
  //     }
  //   });
  // }, []); 

 

  //indexedDb local database

  //creating database
  // async function setupDatabase() {
  //   // Open or create database db1 with version 1
  //   const db = await openDB('db', 1, {
  //     upgrade(db) {
  //       // Create object store 'store'
  //       if (!db.objectStoreNames.contains('store')) {
  //         db.createObjectStore('store', { keyPath: 'id', autoIncrement: true });
  //       }
  //       if (!db.objectStoreNames.contains('brands_with_outlets')) {
  //         db.createObjectStore('brands_with_outlets');
  //       }
  //     },
  //   });
  //   console.log('Databases and object stores created');
  //   return db;
  //   // Open or create database db2 with version 1
  //   // const db2 = await openDB('db2', 1, {
  //   //   upgrade(db) {
  //           // Create object store 'store2'
  //           // db.createObjectStore('store2');
  //   //     // Create object store 'store3' with a keyPath of 'id'
  //   //     db.createObjectStore('store3', { keyPath: 'id' });
  //   //     // Create object store 'store4' with autoIncrementing keys
  //   //     db.createObjectStore('store4', { autoIncrement: true });
  //   //     db.createObjectStore('store5', { keyPath: 'id', autoIncrement: true });
  //   //   },
  //   // });
  // }

  // Save a store in store-table
  // async function saveStore(store) {
  //   const db = await setupDatabase();
  //   await db.add('store', store);
  //   console.log('store saved');
  // }

  // Fetch all records from a specific table (for example, store)
  // async function getAllRecords(tableName) {
  //   const db = await setupDatabase();
  //   const records = await db.getAll(tableName);
  //   return records;
  // }

  // async function deleteTable(dbName, tableName) {
  //   // Open or create the database with an initial version
  //   const db = await openDB(dbName, 2, { // Increment the version from 1 to 2
  //     upgrade(db) {
  //       if (db.objectStoreNames.contains(tableName)) {
  //         db.deleteObjectStore(tableName); // Delete object store
  //       }
  //     },
  //   });
  //   console.log('Table deleted successfully');
  // }

  // function deleteDatabase(dbName) {
  //   const deleteRequest = indexedDB.deleteDatabase(dbName);
  
  //   deleteRequest.onsuccess = () => {
  //     console.log(`Database '${dbName}' deleted successfully`);
  //   };
  
  //   deleteRequest.onerror = () => {
  //     console.error(`Failed to delete the database '${dbName}'`);
  //   };
  
  //   deleteRequest.onblocked = () => {
  //     console.warn(`Deletion of database '${dbName}' is blocked`);
  //   };
  // }
  
  // async function exampleUsage() {
  //   // Save a store
  //   await saveStore({ name: 'lakhan juice center', description: 'Fresh and healthy juices' });
  //   // Fetch all stores
  //   const stores = await getAllRecords('store');
  //   console.log('All stores:', stores);
  
    
    

  //    // Save a customer
  //   //  await saveCustomer({ name: 'John Doe', email: 'john@example.com' });
  //   //  // Save a product
  //   //  await saveProduct({ name: 'Laptop', price: 999.99 });
  //   //  // Save an inventory item
  //   //  await saveInventoryItem({ productId: 1, quantity: 50 });
  //   // // Fetch all customers
  //   // const customers = await getAllRecords('customers');
  //   // console.log('All Customers:', customers);  
  //   // // Fetch all products
  //   // const products = await getAllRecords('products');
  //   // console.log('All Products:', products);

  // }
  
  // Call example usage to see everything in action
  // exampleUsage();
  
  return (
    <div className='page'>
      <Header />
      <SideBar />
      <main id="content">
        <div className="content-inner">
          <Outlet/>
        </div>
      </main>
      <Footer />
      <ToastContainer
        position="top-right"
        hideProgressBar={false}
        autoClose={3000}
        newestOnTop={true}
        closeOnClick={false}
        draggable={false}
        rtl={false}
      />
    </div>
  );
}

export default CompanyLayout;